import { IInteractionsCount } from "@edgetier/watchtower-types";
import { subDays } from "date-fns";
import useInteractionsCounts, { useInteractionsCount } from "hooks-for/interactions/use-interactions-counts";
import useTags from "hooks-for/tags/use-tags";
import { indexByGroup } from "utilities/index-by-group";
import { roundTo } from "utilities/round-to";
import { ANOMALY_INTERACTION_DAYS_PRIOR, ANOMALY_TAG_BAR_COUNT } from "./anomaly-tags-proportion-bars.constants";
import { ITagsProportionBars, ITagsProportionBarsFilters } from "./anomaly-tags-proportion-bars.types";

/**
 * Calculate tag proportion value
 * @param interactionTagCount       Interaction tag count
 * @param interactionAnomalyCount   Anomaly interaction count
 */
export const getTagProportionValue = (
    interactionTagCount: number | undefined,
    interactionAnomalyCount: number | undefined
) => {
    if (
        typeof interactionTagCount !== "number" ||
        typeof interactionAnomalyCount !== "number" ||
        interactionTagCount === 0 ||
        interactionAnomalyCount === 0
    ) {
        return 0;
    }
    return interactionTagCount / interactionAnomalyCount;
};

/**
 * Find tag count value for a tag
 * @param interactionsCount     All interactions count details
 * @param tagId
 */
export const findTagCountValue = (
    interactionsCount: { [group: string]: IInteractionsCount<any> } | undefined,
    tagId: string
) => {
    if (
        typeof interactionsCount === "undefined" ||
        Object.keys(interactionsCount).length === 0 ||
        typeof interactionsCount[tagId] === "undefined"
    ) {
        return 0;
    }

    return interactionsCount[tagId].count;
};

/**
 * Hook used to calculate the proportion bar data for tags
 * @param filters
 */
export const useTagsProportionBarsData = (filters: ITagsProportionBarsFilters) => {
    const { data: tags, isLoading: isTagsLoading } = useTags({ tagGroupId: filters.tagGroupId });

    // Interaction count of anomaly grouped by tagId
    const { data: interactionTagGroupCount, isLoading: isInteractionTagGroupCountLoading } = useInteractionsCounts(
        {
            anomalyId: filters.anomalyId,
            groupBy: filters.groupBy,
            tagGroupId: filters.tagGroupId,
            startDateTime: filters.anomalyStartDateTime,
            endDateTime: filters.anomalyEndDateTime,
        },
        { select: indexByGroup }
    );

    // Interaction count of anomaly grouped by tagId prior a number of days
    const { data: interactionTagGroupPriorDateCount, isLoading: isInteractionTagGroupPriorDateCountLoading } =
        useInteractionsCounts(
            {
                anomalyId: filters.anomalyId,
                groupBy: filters.groupBy,
                tagGroupId: filters.tagGroupId,
                startDateTime: subDays(filters.anomalyStartDateTime, ANOMALY_INTERACTION_DAYS_PRIOR),
                endDateTime: filters.anomalyStartDateTime,
            },
            { select: indexByGroup }
        );

    // Interactions count of anomaly
    const { data: interactionAnomalyCount, isLoading: isLnteractionAnomalyCountLoading } = useInteractionsCount({
        anomalyId: filters.anomalyId,
        startDateTime: filters.anomalyStartDateTime,
        endDateTime: filters.anomalyEndDateTime,
    });

    // Interactions count of anomaly prior a number of days
    const { data: interactionAnomalyPriorCount, isLoading: isInteractionAnomalyPriorCountLoading } =
        useInteractionsCount({
            anomalyId: filters.anomalyId,
            startDateTime: subDays(filters.anomalyStartDateTime, ANOMALY_INTERACTION_DAYS_PRIOR),
            endDateTime: filters.anomalyStartDateTime,
        });

    const tagProportionsBars: ITagsProportionBars[] = (tags ?? [])
        .map((tag) => {
            const tagInteractionCount = findTagCountValue(interactionTagGroupCount, tag.tagId);
            const tagInteractionCountPriorDate = findTagCountValue(interactionTagGroupPriorDateCount, tag.tagId);

            // Proportion for tag interaction count and anomaly interaction count
            const tagProportionValue = roundTo(getTagProportionValue(tagInteractionCount, interactionAnomalyCount), 2);

            // Proportion for tag interaction count prior data and anomaly interaction count prior data
            const tagProportionPriorDateValue = roundTo(
                getTagProportionValue(tagInteractionCountPriorDate, interactionAnomalyPriorCount),
                2
            );

            return {
                tagProportionValue,
                tagProportionPriorDateValue,
                tagProportionBarChange: roundTo(tagProportionValue - tagProportionPriorDateValue, 2),
                tagInteractionCount,
                tag: tag.tag,
                tagId: tag.tagId,
            };
        })
        .sort(
            (one, two) =>
                // Tag proportion bars are sorted by tagProportionPriorDateValue,
                // when values are equal then compare by tagInteractionCount
                Math.abs(two.tagProportionPriorDateValue) - Math.abs(one.tagProportionPriorDateValue) ||
                two.tagInteractionCount - one.tagInteractionCount
        )
        .slice(0, ANOMALY_TAG_BAR_COUNT - 1);

    return {
        tagProportionsBars,
        isLoading:
            isTagsLoading ||
            isInteractionTagGroupCountLoading ||
            isInteractionTagGroupPriorDateCountLoading ||
            isLnteractionAnomalyCountLoading ||
            isInteractionAnomalyPriorCountLoading,
    };
};
